import { createSelector } from 'reselect';

import calculationSelector, { cartStateSelector } from 'Redux/reselects/order-calc/calculation-selector';

// A basket quantity that is not a positive integer counts as 1, so that the
// cart can always be counted and listed
export const toUnits = quantity => (Number.isInteger(quantity) && quantity > 0 ? quantity : 1);

/**
 * Readers of the shared calculation (calculation-selector.js). Each reader is
 * a pure function of the calculation `{ order, error }`, with a selector on
 * top. Money is in integer minor units: use toMajor() from cart-money.js only
 * at the display edge.
 */

const ZERO_TOTALS = Object.freeze({
  total: 0,
  voucherSaving: 0,
  grandTotal: 0,
  fullPrice: 0,
  saving: 0,
});

export const CART_STATUS = Object.freeze({
  NONE: 'none',
  SPEND_LIMIT: 'spendLimit',
  ERROR: 'error',
});

const NONE_STATUS = Object.freeze({ status: CART_STATUS.NONE, code: null, threshold: null });
const ERROR_STATUS = Object.freeze({ status: CART_STATUS.ERROR, code: null, threshold: null });

/**
 * @param   {Object} calculation The result of calculateOrder().
 * @returns {number}             The cart total, minor units. 0 on an error.
 */
export const totalMinor = calculation => calculation.order?.totals.total ?? 0;

/**
 * The money rows of the cart summary, in minor units (the module order).
 *   - `fullPrice`: the full price of the cart (`totals.fullPrice`, stage 7 D1).
 *     When a line has a reference price (a pay what you want bundle, order-calc
 *     1.0.7, catalogue D30), it is `totals.referenceFullPrice`: what the games
 *     are worth. The module charges the pledge, so `fullPrice` alone shows no
 *     saving there.
 *   - `saving`: the promotion saving (`totals.promoSaving`), before the voucher.
 *     With a reference price, `totals.referenceSaving`.
 *   - `grandTotal`: the total before the voucher (`total + voucherSaving`).
 *   - `voucherSaving` and `total`: the module totals.
 * All 0 on an error, as the error cart.
 *
 * @param   {Object} calculation The result of calculateOrder().
 * @returns {Object}             { total, voucherSaving, grandTotal, fullPrice, saving }
 */
export const cartTotals = (calculation) => {
  const { order } = calculation;

  if (!order) return ZERO_TOTALS;

  const {
    total,
    voucherSaving,
    fullPrice,
    promoSaving,
    referenceFullPrice,
    referenceSaving,
  } = order.totals;

  return Object.freeze({
    total,
    voucherSaving,
    grandTotal: total + voucherSaving,
    fullPrice: referenceFullPrice ?? fullPrice,
    saving: referenceSaving ?? promoSaving,
  });
};

/**
 * The cart items, one entry for each unit (a product with quantity 2 counts
 * 2). The badge and the analytics count units. On an error, the module has no
 * lines, so the count comes from the basket (a quantity that is not a positive
 * integer counts as 1, as in the error cart).
 *
 * @param   {Object} calculation The result of calculateOrder().
 * @param   {Object} cart        state.cart
 * @returns {number}             The number of units.
 */
export const unitCount = (calculation, cart) => {
  if (calculation.order) return calculation.order.lines.length;

  return (cart?.items ?? [])
    .filter(Boolean)
    .reduce((count, item) => count + toUnits(item.quantity), 0);
};

/**
 * What the cart shows next to the checkout button.
 *   - `error`: order-calc could not calculate the cart. Checkout stops.
 *   - `spendLimit`: a voucher failed its min or max spend (`code` MIN_SPEND or
 *     MAX_SPEND, `threshold` in minor units). The voucher is not applied. The
 *     message shows and checkout does not stop (stage 6 decision 20).
 *   - `none`: nothing. This includes NO_ELIGIBLE_ITEMS (it has no threshold)
 *     and an empty cart.
 *
 * @param   {Object} calculation The result of calculateOrder().
 * @returns {Object}             { status, code, threshold }
 */
export const cartStatus = (calculation) => {
  const { order } = calculation;

  if (!order) return ERROR_STATUS;

  const rejection = order.voucher?.rejection;

  if (!rejection || !order.lines.length || rejection.code === 'NO_ELIGIBLE_ITEMS') {
    return NONE_STATUS;
  }

  return Object.freeze({
    status: CART_STATUS.SPEND_LIMIT,
    code: rejection.code,
    threshold: rejection.threshold,
  });
};

/**
 * The item ids that the voucher does not apply to (one entry for each
 * excluded unit). The cards check it with `includes`.
 *
 * @param   {Object|null} order The module order.
 * @returns {string[]}          Item ids. [] with no order or no voucher.
 */
export const excludedItemIds = order => (order?.voucher?.excludedLineIds ?? [])
  .map(lineId => order.lines.find(line => line.lineId === lineId).itemId);

export const cartTotalsSelector = createSelector(calculationSelector, cartTotals);

export const cartTotalMinorSelector = createSelector(calculationSelector, totalMinor);

export const cartUnitCountSelector = createSelector(
  calculationSelector,
  cartStateSelector,
  unitCount,
);

export const cartStatusSelector = createSelector(calculationSelector, cartStatus);

export const excludedItemIdsSelector = createSelector(
  calculationSelector,
  calculation => excludedItemIds(calculation.order),
);
